import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import type { RawCoordinates } from '@elfsight/widget-map';
import { useSettings } from '@wu/widget-core';
import { noop } from 'es-toolkit';
import type { StoreLocatorLocation } from '../types';
import { useLocationsFilter } from './locations-filter-provider';
import { getDistance } from './utils/get-distance';

type UserLocationType = {
  coordinates: RawCoordinates;
  address?: string;
};

export type IUserLocationContext = {
  userLocation?: UserLocationType;
  setUserLocation: (coordinates: RawCoordinates) => void;
  locations: (StoreLocatorLocation & { distance?: number })[];
  refreshUserPosition: () => void;
};

const UserLocationContext = createContext<IUserLocationContext>({
  userLocation: undefined,
  setUserLocation: noop,
  locations: [],
  refreshUserPosition: noop
});

export function UserLocationProvider({ children }: PropsWithChildren) {
  const { directoryShowSearch, allowGetUserLocation } = useSettings();

  const { currentLocations } = useLocationsFilter();

  const [userLocation, setUserLocation] = useState<
    UserLocationType | undefined
  >();

  const refreshUserPosition = useCallback(() => {
    if (!allowGetUserLocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const { latitude, longitude } = coords;

        setUserLocation({
          coordinates: {
            lng: longitude,
            lat: latitude
          }
        });
      },
      () => {
        setUserLocation(undefined);
      }
    );
  }, [allowGetUserLocation, setUserLocation]);

  useEffect(() => {
    if (!directoryShowSearch || !allowGetUserLocation || userLocation) {
      return;
    }

    refreshUserPosition();
  }, [
    directoryShowSearch,
    allowGetUserLocation,
    userLocation,
    refreshUserPosition
  ]);

  useEffect(() => {
    if (!directoryShowSearch || !allowGetUserLocation) {
      setUserLocation(undefined);
    }
  }, [directoryShowSearch, allowGetUserLocation]);

  const orderedLocations = useMemo(() => {
    if (!userLocation) {
      return currentLocations;
    }

    return currentLocations
      .map((location) => ({
        ...location,
        distance: getDistance(
          location.place?.coordinates,
          userLocation.coordinates
        )
      }))
      .sort((a, b) => {
        if (a.distance === undefined && b.distance === undefined) {
          return 0;
        }

        if (a.distance === undefined) {
          return 1;
        }

        if (b.distance === undefined) {
          return -1;
        }

        return a.distance - b.distance;
      });
  }, [currentLocations, userLocation]);

  const context = useMemo(
    () => ({
      userLocation,
      setUserLocation: (coordinates: RawCoordinates) =>
        setUserLocation({ coordinates }),
      locations: orderedLocations,
      refreshUserPosition
    }),
    [userLocation, orderedLocations]
  );

  return (
    <UserLocationContext.Provider value={context}>
      {children}
    </UserLocationContext.Provider>
  );
}

export const useUserLocation = () => {
  return useContext(UserLocationContext);
};
